/**
 * 过磅设备配置 — 录像机 + 摄像头通道 + 地磅(小磅/大磅)↔串口↔摄像头 绑定.
 * 全部本机级, 存 localStorage (工厂实施时在「系统管理 → 过磅设备」页里配).
 *
 * 背景: 摄像头跟着地磅一组, 小磅/大磅各两个摄像头. 过磅用哪个磅, 记录毛重/皮重就抓那个磅的摄像头.
 * 为什么存本机: 地磅走浏览器 Web Serial, 串口授权本就是本机/浏览器级的; 录像机/摄像头也按本机配.
 *
 * 地磅 key 跟 useScale(which) 一致: entry=小磅, exit=大磅. 对应 weighbridgeMixin 的 scaleKey: entryScale / exitScale.
 */

import store from '@/store'
import ScaleClient from './scaleClient'

// 地磅协议代号 — 前后端(jinglunGo scale_protocol.go)共用同一套代号按 protocol 分发解析器
// yaohua-continuous: 上海耀华 DS3/A9/A9+/A12 等 tF=0 连续发送模式(12字节定长帧);
//                    柯力 D 系列 TF=0(连续方式1)与此逐字节相同, 同样选这一项
// ht9800-continuous: 杭州衡天 HT9800 连续发送模式(8字节帧, 整帧字节序反转, 无小数位)
// keli-continuous:   柯力 D 系列 D2+ 倒序连续(TF=2 老8字节 / TF=3 新9字节, "5.88100=" 式)
// xk3101-continuous: 柯力 XK3101 连续方式1(正序 "=01234.5\r\n")
// toledo-continuous: 托利多 T800/8142 兼容连续输出(18字节 STX+状态字+毛皮重+CR+CKS)
// stgs-continuous:   打印式 ASCII 连续("ST,GS,+00188.5kg\r\n", 柯力 TF=7 / 托利多 ST-GS 式)
// pt650d-continuous: 志美/至美 PT650D 连续("ST,GS,+   1234kg\r\n" 空格补位; 串口 7-E-1 偶校验, 与其它协议不同)
// [2026-08-09] 协议解析已迁移到 jinglunGo 后端（scale_protocol.go），前端只存配置值
const PROTOCOL_YAOHUA_CONTINUOUS = 'yaohua-continuous'
const PROTOCOL_HT9800_CONTINUOUS = 'ht9800-continuous'
const PROTOCOL_KELI_CONTINUOUS = 'keli-continuous'
const PROTOCOL_XK3101_CONTINUOUS = 'xk3101-continuous'
const PROTOCOL_TOLEDO_CONTINUOUS = 'toledo-continuous'
const PROTOCOL_STGS_CONTINUOUS = 'stgs-continuous'
const PROTOCOL_PT650D_CONTINUOUS = 'pt650d-continuous'
const PROTOCOL_OPTIONS = [
  PROTOCOL_YAOHUA_CONTINUOUS,
  PROTOCOL_HT9800_CONTINUOUS,
  PROTOCOL_KELI_CONTINUOUS,
  PROTOCOL_XK3101_CONTINUOUS,
  PROTOCOL_TOLEDO_CONTINUOUS,
  PROTOCOL_STGS_CONTINUOUS,
  PROTOCOL_PT650D_CONTINUOUS
]

// [2026-09-26] 设备配置改为机器级共用（不再按工厂隔离）: 读卡器/地磅/监控都是插在
// 本机或与本机同网的物理设备, 配置属于机器而不属于登录账号的工厂 —— 同一台电脑
// 不管登录哪个工厂的账号, 设备配置共用同一份。历史沿革: [2026-05-29] 曾按工厂隔离
// (key 拼 ::工厂ID), [2026-09-26] 废除该隔离并做反向迁移(见 readScoped)。
const BIND_KEY = 'drying_scale_binding_v1'
// 与 HikvisionPreviewGrid.vue 同一份数据结构 (录像机 + 通道); 两边都用本文件的 cameraConfigKey()/readCameraConfigRaw()
const CAMERA_CONFIG_KEY = 'drying_hikvision_camera_config_v1'
const CAMERA_CONFIG_VERSION = 2

function currentFactoryId() {
  try {
    const s = (store && store.state) || {}
    return (s.externalOrg && s.externalOrg.saleOrgId) || (s.loginUser && s.loginUser.externalOrgId) || ''
  } catch (e) { return '' }
}
// [2026-09-26] 机器级: 不再拼工厂后缀。保留函数名兼容全部既有调用点与 HikvisionPreviewGrid。
export function scopedKey(base) {
  return base
}
// 读取并一次性做"工厂隔离时代 → 机器级"的反向迁移:
// 基础 key 为空而老的 base::工厂ID key 有数据时, 把数据搬回基础 key。
// 多个工厂维度都有数据时优先搬"当前登录工厂"的（本机最近使用视角）, 其余工厂维度
// 的残留 key 一并清除（机器级化后没有任何代码会再读它们）。
function readScoped(base) {
  let raw = null
  try { raw = localStorage.getItem(base) } catch (e) { return null }
  if (raw == null) {
    const suffixes = []
    const cur = currentFactoryId()
    if (cur) suffixes.push(cur)
    let allKeys = []
    try { allKeys = Object.keys(localStorage) } catch (e) { allKeys = [] }
    for (const k of allKeys) {
      if (k && k.indexOf(base + '::') === 0) {
        const sfx = k.slice(base.length + 2)
        if (suffixes.indexOf(sfx) < 0) suffixes.push(sfx)
      }
    }
    for (const sfx of suffixes) {
      const scoped = base + '::' + sfx
      let v = null
      try { v = localStorage.getItem(scoped) } catch (e) { v = null }
      if (v != null) {
        raw = v
        try { localStorage.setItem(base, v) } catch (e) {}
        break
      }
    }
    // 迁移完成（或确认无老数据）后清掉所有工厂维度的残留 key
    try {
      for (const k of Object.keys(localStorage)) {
        if (k && k.indexOf(base + '::') === 0) localStorage.removeItem(k)
      }
    } catch (e) { /* ignore */ }
  }
  return raw
}
// 供 HikvisionPreviewGrid 复用, 保证两边用同一个机器级 key
export function cameraConfigKey() { return scopedKey(CAMERA_CONFIG_KEY) }
export function readCameraConfigRaw() { return readScoped(CAMERA_CONFIG_KEY) }

// 默认两个磅: 小磅(entry)=CH1,CH2; 大磅(exit)=CH3,CH4
// port: 用户在配置页点"选择串口"后记下的串口描述符 { usbVendorId, usbProductId, occurrence } | null
//       (Web Serial 拿不到 COM 名, 只能按 USB 厂商号/产品号 + 同型号第几个 来记住和匹配)
// serialIndex: 没用 port 时的兜底 (按 getPorts() 顺序的第几个)
// enabled: [2026-07-31] 磅启用开关. 有的烘干厂只有一个地磅, 把大磅关掉后, 大磅的一切(顶栏/卡片/自动连接/抓拍)都不触发.
//          默认两个都启用; 最少保留一个启用 (配置页会拦截禁用最后一个).
export const DEFAULT_SCALES = [
  { key: 'entry', name: '小磅', baudRate: 9600, serialIndex: 0, port: null, comPort: null, protocol: PROTOCOL_YAOHUA_CONTINUOUS, cameraChannelIds: [1, 2], enabled: true },
  { key: 'exit', name: '大磅', baudRate: 9600, serialIndex: 1, port: null, comPort: null, protocol: PROTOCOL_YAOHUA_CONTINUOUS, cameraChannelIds: [3, 4], enabled: true }
]

// [重构] 通道↔磅绑定归属到通道本身 (通道→磅方向, scaleKey: 'entry'|'exit'|'').
// 与 DEFAULT_SCALES 默认一致: entry(小磅)→CH1,CH2; exit(大磅)→CH3; 卸货区不绑磅.
const DEFAULT_CHANNELS = [
  { name: '小磅入车口', channelId: 1, streamType: 1, scaleKey: 'entry' },
  { name: '小磅出车口', channelId: 2, streamType: 1, scaleKey: 'entry' },
  { name: '大磅入车口', channelId: 3, streamType: 1, scaleKey: 'exit' },
  { name: '卸货区', channelId: 4, streamType: 1, scaleKey: '' }
]

// entry/exit → weighbridgeMixin 用的 scaleKey
const SCALE_KEY_MAP = { entry: 'entryScale', exit: 'exitScale' }

function clone(v) {
  return JSON.parse(JSON.stringify(v))
}

function toInt(v, fallback) {
  const n = parseInt(v, 10)
  return isNaN(n) ? fallback : n
}

function normChannel(c) {
  return {
    name: (c && c.name) || ('通道' + (c && c.channelId)),
    channelId: toInt(c && c.channelId, 0),
    streamType: toInt(c && c.streamType, 1) === 2 ? 2 : 1,
    // [多品牌] custom 品牌时用户自填的完整 RTSP 地址
    rtspUrl: (c && c.rtspUrl) || '',
    // [无中心模式] 通道级连接覆盖: 空=继承顶层录像机配置 (nvr 模式忽略这些字段)
    ip: (c && c.ip) || '',
    username: (c && c.username) || '',
    password: (c && c.password) || '',
    rtspPort: toInt(c && c.rtspPort, 0),
    // [重构] 通道↔磅绑定归通道管: 'entry'(小磅) | 'exit'(大磅) | ''(未绑定)
    scaleKey: (c && (c.scaleKey === 'entry' || c.scaleKey === 'exit')) ? c.scaleKey : ''
  }
}

// ================== 地磅绑定 ==================

/** 读地磅↔串口↔摄像头绑定 (没存过返回默认两个磅) */
export function loadScaleBinding() {
  try {
    const raw = readScoped(BIND_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      if (obj && Array.isArray(obj.scales) && obj.scales.length) {
        return DEFAULT_SCALES.map(def => {
          const hit = obj.scales.find(s => s.key === def.key) || {}
          return {
            key: def.key,
            name: hit.name || def.name,
            baudRate: toInt(hit.baudRate, def.baudRate),
            serialIndex: toInt(hit.serialIndex, def.serialIndex),
            port: (hit.port && hit.port.usbVendorId != null) ? {
              usbVendorId: hit.port.usbVendorId,
              usbProductId: hit.port.usbProductId,
              occurrence: toInt(hit.port.occurrence, 0)
            } : null,
            // [2026-07-26] 桌面专用 COM 口（仅 webbox 环境，浏览器环境为 null）
            comPort: (typeof hit.comPort === 'string' && hit.comPort.trim()) ? hit.comPort.trim().toUpperCase() : null,
            protocol: PROTOCOL_OPTIONS.includes(hit.protocol) ? hit.protocol : def.protocol,
            cameraChannelIds: Array.isArray(hit.cameraChannelIds)
              ? hit.cameraChannelIds.map(Number).filter(n => !isNaN(n))
              : def.cameraChannelIds.slice(),
            // [2026-07-31] 磅启用开关: 旧配置没有此字段时默认启用 (向后兼容)
            enabled: hit.enabled !== false
          }
        })
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadScaleBinding 解析失败, 用默认:', e)
  }
  return clone(DEFAULT_SCALES)
}

/** 存地磅绑定 */
export function saveScaleBinding(scales) {
  const out = (scales || []).map(s => ({
    key: s.key,
    name: s.name || (s.key === 'entry' ? '小磅' : '大磅'),
    baudRate: toInt(s.baudRate, 9600),
    serialIndex: toInt(s.serialIndex, 0),
    port: (s.port && s.port.usbVendorId != null) ? {
      usbVendorId: s.port.usbVendorId,
      usbProductId: s.port.usbProductId,
      occurrence: toInt(s.port.occurrence, 0)
    } : null,
    // [2026-07-26] 桌面专用 COM 口
    comPort: (typeof s.comPort === 'string' && s.comPort.trim()) ? s.comPort.trim().toUpperCase() : null,
    protocol: PROTOCOL_OPTIONS.includes(s.protocol) ? s.protocol : PROTOCOL_YAOHUA_CONTINUOUS,
    cameraChannelIds: (s.cameraChannelIds || []).map(Number).filter(n => !isNaN(n)),
    // [2026-07-31] 磅启用开关
    enabled: s.enabled !== false
  }))
  localStorage.setItem(scopedKey(BIND_KEY), JSON.stringify({ scales: out }))
  _markWeighConfigDirty(BIND_KEY)
}

/**
 * [重构] 取某个磅 (entry/exit) 绑定的摄像头通道 id 列表.
 * 数据源以"通道上的 scaleKey"为准 (通道→磅方向); 通道都没标 scaleKey(全新/异常) 时,
 * 回退到 DEFAULT_SCALES 的 cameraChannelIds 兜底, 保证抓拍不中断.
 */
export function channelIdsForScale(scaleKey) {
  const channels = loadCameraChannels()
  const ids = channels
    .filter(c => c.scaleKey === scaleKey)
    .map(c => Number(c.channelId))
    .filter(n => !isNaN(n))
  if (ids.length) return ids
  // 兜底: 通道都没标绑定时, 用默认磅绑定 (entry→CH1,CH2; exit→CH3,CH4)
  const s = loadScaleBinding().find(x => x.key === scaleKey)
  return (s && Array.isArray(s.cameraChannelIds)) ? s.cameraChannelIds.slice() : []
}

/** 取某个磅 (entry/exit) 绑定的通道名 (展示用) */
export function channelNamesForScale(scaleKey) {
  const ids = channelIdsForScale(scaleKey)
  const channels = loadCameraChannels()
  return ids.map(id => {
    const c = channels.find(x => Number(x.channelId) === Number(id))
    return c ? c.name : ('CH' + id)
  })
}

/** 取某个磅 (entry/exit) 的显示名 */
export function scaleName(scaleKey) {
  const s = loadScaleBinding().find(x => x.key === scaleKey)
  return (s && s.name) || (scaleKey === 'entry' ? '小磅' : '大磅')
}

/**
 * [2026-07-31] 磅启用状态映射: { entry: true, exit: false }.
 * 供顶栏/过磅卡片/弹窗判断某个磅是否启用 (禁用后该磅的一切都不触发).
 * 旧配置无 enabled 字段时默认启用.
 */
export function scaleEnabledMap() {
  const map = { entry: true, exit: true }
  loadScaleBinding().forEach(s => {
    if (s.key === 'entry' || s.key === 'exit') map[s.key] = s.enabled !== false
  })
  return map
}

/** [2026-07-31] 判断某个磅 (entry/exit) 是否启用 */
export function isScaleEnabled(scaleKey) {
  return scaleEnabledMap()[scaleKey] !== false
}

/** weighbridgeMixin 自动连接用的串口序号映射: { entryScale: 0, exitScale: 1 } */
export function scaleSerialMap() {
  const map = {}
  loadScaleBinding().forEach(s => {
    const k = SCALE_KEY_MAP[s.key]
    if (k) map[k] = toInt(s.serialIndex, 0)
  })
  return map
}

/** 波特率映射: { entryScale: 9600, exitScale: 9600 } */
export function scaleBaudMap() {
  const map = {}
  loadScaleBinding().forEach(s => {
    const k = SCALE_KEY_MAP[s.key]
    if (k) map[k] = toInt(s.baudRate, 9600)
  })
  return map
}

/** 协议映射: { entryScale: 'yaohua-continuous', exitScale: 'yaohua-continuous' } */
export function scaleProtocolMap() {
  const map = {}
  loadScaleBinding().forEach(s => {
    const k = SCALE_KEY_MAP[s.key]
    if (k) map[k] = s.protocol || PROTOCOL_YAOHUA_CONTINUOUS
  })
  return map
}

/** 配置页下拉用: 可选协议列表 + 中文标签 */
export const SCALE_PROTOCOL_OPTIONS = [
  { value: PROTOCOL_YAOHUA_CONTINUOUS, label: '耀华连续模式 (DS3/A9/A12)' },
  { value: PROTOCOL_HT9800_CONTINUOUS, label: '衡天 HT9800 连续模式' },
  { value: PROTOCOL_KELI_CONTINUOUS, label: '柯力 D2+ 连续模式 (D2008/D2002/D11)' },
  { value: PROTOCOL_XK3101_CONTINUOUS, label: '柯力 XK3101 连续模式' },
  { value: PROTOCOL_TOLEDO_CONTINUOUS, label: '托利多连续模式 (T800/8142)' },
  { value: PROTOCOL_STGS_CONTINUOUS, label: '打印式 ASCII 连续 (ST,GS)' },
  { value: PROTOCOL_PT650D_CONTINUOUS, label: '志美/至美 PT650D 连续 (7-E-1 偶校验)' }
]

/** 协议代号 → 中文标签(配置页/检测结论展示用; 未知代号回退原字符串) */
export function scaleProtocolLabel(protocol) {
  const hit = SCALE_PROTOCOL_OPTIONS.find(o => o.value === protocol)
  return hit ? hit.label : (protocol || '')
}

// ================== 已通地磅端口池 (Web Serial 模式用，机器级) ==================
//
// 独立于 entry/exit 绑定(scales)的「端口 → 已验证波特率」缓存.
// 设计目的: Web Serial 模式下用户首点"连接"时遍历 [1200,2400,4800,9600] 探测, 命中后把
// "这个串口 + 哪档波特率 + 协议"记下来; 下次连接(含进页面自动连接)直接用记住的波特率直连, 不再遍历.
// 与 scales 的关系: scales.port 决定"entry/exit 各绑哪个物理串口"; 端口池回答"这个串口通哪个波特率".
// 两者解耦 — 端口池不取代 scales 绑定, 只缓存探测结果.
// [2026-08-10] 仅 Web Serial 模式(jinglunGo 模式由后端管波特率探测，不用端口池)
//
// 池条目结构: { id, name, port:{usbVendorId,usbProductId,occurrence}, baudRate, protocol, verifiedAt }

const SCALE_PORT_POOL_KEY = 'drying_scale_port_pool_v1'

// 候选波特率(探测遍历用). 顺序按"最常见在前": 耀华仪表 9600 居多, 放首档让多数现场 <1s 命中.
export const SCALE_PROBE_BAUD_RATES = [9600, 4800, 2400, 1200]

// 生成池条目 id (时间戳 + 随机, 避免短时连续添加碰撞)
function _genPoolId() {
  return 'port_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
}

/**
 * 判断两个端口描述符是否指向同一物理串口(vid/pid/occurrence 三者全同).
 * 任一为空 / 缺 usbVendorId 视为不等.
 */
export function samePortDesc(a, b) {
  if (!a || !b || a.usbVendorId == null || b.usbVendorId == null) return false
  return Number(a.usbVendorId) === Number(b.usbVendorId) &&
    Number(a.usbProductId) === Number(b.usbProductId) &&
    Number(a.occurrence || 0) === Number(b.occurrence || 0)
}

/** 在池里找指向 desc 的条目(同 vid/pid/occurrence); 找不到返回 null */
export function findPoolEntry(pool, desc) {
  const list = Array.isArray(pool) ? pool : []
  return list.find(e => samePortDesc(e.port, desc)) || null
}

/**
 * 取某个端口在池里已验证的波特率; 池里没有或条目无效返回 null.
 * Web Serial 自动连接/直连优先用这个值, 命中即跳过遍历.
 */
export function poolVerifiedBaud(desc) {
  const hit = findPoolEntry(loadScalePortPool(), desc)
  return (hit && Number(hit.baudRate) > 0) ? Number(hit.baudRate) : null
}

/**
 * 读已通地磅端口池(没存过返回空数组).
 * @returns {Array<{id,name,port,baudRate,protocol,verifiedAt,_rk:string}>}
 */
export function loadScalePortPool() {
  try {
    const raw = readScoped(SCALE_PORT_POOL_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      if (obj && Array.isArray(obj.pool)) {
        return obj.pool.map(e => _normPoolEntry(e)).filter(Boolean)
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadScalePortPool 解析失败, 用空池:', e)
  }
  return []
}

// 规范化一条池条目; 非法返回 null(被 load 过滤掉)
function _normPoolEntry(e) {
  if (!e || !e.port || e.port.usbVendorId == null) return null
  return {
    id: e.id || _genPoolId(),
    name: String(e.name || '').slice(0, 30),
    port: {
      usbVendorId: Number(e.port.usbVendorId),
      usbProductId: Number(e.port.usbProductId),
      occurrence: toInt(e.port.occurrence, 0)
    },
    baudRate: Number(e.baudRate) > 0 ? Number(e.baudRate) : 9600,
    protocol: PROTOCOL_OPTIONS.includes(e.protocol) ? e.protocol : PROTOCOL_YAOHUA_CONTINUOUS,
    verifiedAt: Number(e.verifiedAt) > 0 ? Number(e.verifiedAt) : 0
  }
}

/** 存已通地磅端口池 */
export function saveScalePortPool(pool) {
  const out = (pool || []).map(e => _normPoolEntry(e)).filter(Boolean)
  localStorage.setItem(scopedKey(SCALE_PORT_POOL_KEY), JSON.stringify({ pool: out }))
  _markWeighConfigDirty(SCALE_PORT_POOL_KEY)
}

/**
 * 新增或更新一条池条目(按 port 描述符去重). 探测命中时调用此函数把结果写回.
 * @param {{port:{usbVendorId,usbProductId,occurrence}, baudRate:number, protocol?:string, name?:string}} entry
 * @returns {Object} 实际写入/更新后的池(数组)
 */
export function upsertScalePortPool(entry) {
  const pool = loadScalePortPool()
  const normed = _normPoolEntry({ ...entry, id: (findPoolEntry(pool, entry.port) || {}).id, verifiedAt: Date.now() })
  if (!normed) return pool
  const idx = pool.findIndex(e => samePortDesc(e.port, entry.port))
  if (idx >= 0) {
    // 已存在: 保留旧 name(除非新值非空), 更新 baud/protocol/verifiedAt
    pool[idx] = { ...pool[idx], ...normed, name: normed.name || pool[idx].name }
  } else {
    pool.push(normed)
  }
  saveScalePortPool(pool)
  return pool
}

/** 串口描述符映射（Web Serial 模式）: { entryScale: {usbVendorId,usbProductId,occurrence}|null, exitScale: ... } */
export function scalePortMap() {
  const map = {}
  loadScaleBinding().forEach(s => {
    const k = SCALE_KEY_MAP[s.key]
    if (k) map[k] = s.port || null
  })
  return map
}

/**
 * COM 口映射（jinglunGo 模式）: { entryScale: 'COM3', exitScale: 'COM5' }
 * jinglunGo 后端按 COM 名直开串口。
 */
export function scaleComPortMap() {
  const map = {}
  loadScaleBinding().forEach(s => {
    const k = SCALE_KEY_MAP[s.key]
    if (k) map[k] = s.comPort || null
  })
  return map
}

// hex(4 位) 格式化, 给串口描述符标签用
function _hex4(n) {
  return n == null ? '' : Number(n).toString(16).toUpperCase().padStart(4, '0')
}

/**
 * 某个磅 (entry/exit) 绑定串口的可读标签 (过磅页地磅卡展示用).
 * 优先显示 COM 口名（jinglunGo 模式 / 桌面），没有则按 Web Serial 的 USB 描述符组合标签。
 * @param {string} which - 'entry' | 'exit'
 * @returns {string} 没绑定串口返回 ''
 */
export function scalePortLabel(which) {
  const s = loadScaleBinding().find(x => x.key === which)
  // 优先 COM 口名（jinglunGo 模式）
  if (s && s.comPort) return String(s.comPort).toUpperCase()
  // 回退 Web Serial 的 USB 描述符标签
  const port = s && s.port
  if (!port || port.usbVendorId == null) return ''
  const vendor = Number(port.usbVendorId) === 0x110a ? 'MOXA' : 'USB串口'
  const id = ` ${_hex4(port.usbVendorId)}:${_hex4(port.usbProductId)}`
  const occ = port.occurrence ? ` ·第${port.occurrence + 1}个` : ''
  return vendor + id + occ
}

// ================== 地磅连接方式开关 ==================
// [2026-08-10] 用户在「过磅设备配置」页选择地磅走哪条路径：
//   'web'：浏览器 Web Serial API
//   'jinglun'：jinglunGo 本地服务
// [2026-08-29] 默认值从 'web' 改为 'jinglun'（傻瓜模式：装好粮宝即用，无需浏览器授权串口）。
//   只有"曾经显式选过 web"的机器继续走 web（localStorage 里存了 {mode:'web'}），其余全部默认 jinglun。
const SCALE_CONNECT_MODE_KEY = 'drying_scale_connect_mode_v1'

/** 读地磅连接方式（[2026-08-29] 起默认 'jinglun'；显式存过 'web' 的保持 web） */
export function getScaleConnectMode() {
  try {
    const raw = readScoped(SCALE_CONNECT_MODE_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      if (obj && obj.mode === 'web') return 'web'
    }
  } catch (e) { /* 用默认 */ }
  return 'jinglun'
}

/** 写地磅连接方式 */
export function setScaleConnectMode(mode) {
  const m = mode === 'jinglun' ? 'jinglun' : 'web'
  localStorage.setItem(scopedKey(SCALE_CONNECT_MODE_KEY), JSON.stringify({ mode: m }))
  _markWeighConfigDirty(SCALE_CONNECT_MODE_KEY)
  return m
}

/** 地磅连接方式选项（配置页下拉用） */
export const SCALE_CONNECT_MODE_OPTIONS = [
  { value: 'jinglun', label: 'jinglunGo 本地服务（默认，推荐 —— 装好粮宝即用）' },
  { value: 'web', label: '浏览器 Web Serial（备选，Chrome/Edge 直连）' }
]

// ================== 录像机 + 摄像头通道 (与 HikvisionPreviewGrid 同一份) ==================

/**
 * [重构] 老数据迁移: 旧版通道没有 scaleKey, 通道↔磅绑定存在"磅→channelIds"(DEFAULT_SCALES/loadScaleBinding).
 * 这里用旧的磅→channelIds 反推回填到每个通道的 scaleKey 上, 一次性、向下兼容.
 * 仅在通道 scaleKey 为空(老配置)时回填, 已有 scaleKey 的新配置不动.
 */
function migrateChannelScaleKey(channels, fallbackScaleBinding) {
  const list = Array.isArray(channels) ? channels : []
  if (!list.length) return list
  const needMigration = list.some(c => !c || !c.scaleKey)
  if (!needMigration) return list
  // 磅 key → channelId 集合 (从旧的磅→通道绑定里取)
  const mapByScale = {}
  ;(fallbackScaleBinding || []).forEach(s => {
    (s.cameraChannelIds || []).forEach(id => {
      mapByScale[Number(id)] = s.key
    })
  })
  return list.map(c => {
    if (c && c.scaleKey) return c
    const id = Number(c && c.channelId)
    const scaleKey = mapByScale[id] || ''
    return { ...c, scaleKey: (scaleKey === 'entry' || scaleKey === 'exit') ? scaleKey : '' }
  })
}

/** 读录像机 + 通道配置 (没配过返回默认) */
export function loadCameraConfig() {
  const def = {
    version: CAMERA_CONFIG_VERSION,
    ip: '',
    brand: 'hikvision',
    provider: 'native',
    // [无中心模式] 连接拓扑: 'nvr'(中心录像机,默认) | 'direct'(摄像头直连,每通道独立IP); direct 强制 provider=go2rtc
    topology: 'nvr',
    protocol: 'http',
    httpPort: 80,
    rtspPort: 554,
    // 全局默认码流 (1=主码流 默认; 2=子码流), 在系统管理"监控高级设置"里配, 各通道共用
    streamType: 1,
    // go2rtc 抓拍方式: 'auto'(自动降级) | 'framejpeg'(服务端截图,需ffmpeg) | 'webrtc'(浏览器截图)
    captureMode: 'auto',
    // [2026-10-13] 海康原生模式: 隐藏过磅页工具栏「监控配置」按钮(录像机配好后日常不再需要); 默认 false=显示
    hideNativeConfigBtn: false,
    username: '',
    password: '',
    channels: clone(DEFAULT_CHANNELS)
  }
  try {
    const raw = readScoped(CAMERA_CONFIG_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      const channels = (Array.isArray(obj.channels) && obj.channels.length)
        ? migrateChannelScaleKey(obj.channels.map(normChannel), loadScaleBinding())
        : clone(DEFAULT_CHANNELS)
      return {
        ...def,
        ...obj,
        brand: obj.brand || 'hikvision',
        provider: obj.provider || ((obj.brand && obj.brand !== 'hikvision') ? 'go2rtc' : 'native'),
        topology: obj.topology === 'direct' ? 'direct' : 'nvr',
        ip: obj.ip || '',
        protocol: obj.protocol === 'https' ? 'https' : 'http',
        httpPort: toInt(obj.httpPort, 80),
        rtspPort: toInt(obj.rtspPort, 554),
        // 全局码流: 取存档顶层值, 没有则回退到第一个通道的码流, 再没有用默认主码流(1)
        streamType: toInt(obj.streamType, toInt(channels[0] && channels[0].streamType, 1)) === 2 ? 2 : 1,
        // 抓拍方式: 只允许三个合法值, 非法兜底 auto
        captureMode: (obj.captureMode === 'framejpeg' || obj.captureMode === 'webrtc') ? obj.captureMode : 'auto',
        // 海康原生「监控配置」按钮隐藏开关: 仅显式 true 才隐藏
        hideNativeConfigBtn: obj.hideNativeConfigBtn === true,
        channels
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadCameraConfig 解析失败, 用默认:', e)
  }
  return def
}

/** 存录像机 + 通道配置 (结构与 HikvisionPreviewGrid 完全一致, 组件能直接读) */
export function saveCameraConfig(cfg) {
  const out = {
    version: CAMERA_CONFIG_VERSION,
    brand: cfg.brand || 'hikvision',
    provider: cfg.provider === 'go2rtc' ? 'go2rtc' : 'native',
    topology: cfg.topology === 'direct' ? 'direct' : 'nvr',
    ip: cfg.ip || '',
    protocol: cfg.protocol === 'https' ? 'https' : 'http',
    httpPort: toInt(cfg.httpPort, 80),
    rtspPort: toInt(cfg.rtspPort, 554),
    streamType: toInt(cfg.streamType, 1) === 2 ? 2 : 1,
    captureMode: (cfg.captureMode === 'framejpeg' || cfg.captureMode === 'webrtc') ? cfg.captureMode : 'auto',
    hideNativeConfigBtn: cfg.hideNativeConfigBtn === true,
    username: cfg.username || '',
    password: cfg.password || '',
    channels: (cfg.channels || []).map(normChannel)
  }
  localStorage.setItem(scopedKey(CAMERA_CONFIG_KEY), JSON.stringify(out))
  _markWeighConfigDirty(CAMERA_CONFIG_KEY)
}

/** 读摄像头通道列表 (没配过返回默认 4 路) */
export function loadCameraChannels() {
  return loadCameraConfig().channels
}

// ================== NFC 轮询开关 ==================

// [2026-07-27] NFC 轮询固定开启, 不再可配置.
// 入库/出库页自动轮询烘干厂外设服务(127.0.0.1:8765)来被动刷卡调单.
// 旧的 drying_nfc_polling_enabled_v1 localStorage 项已废弃, loadNfcPollingEnabled 恒返回 true.
const NFC_POLLING_KEY = 'drying_nfc_polling_enabled_v1'

// ================== 地磅读数自动跟随开关 (机器级) ==================

// 过磅页表单的毛重/皮重是否自动跟随地磅实时读数.
// 关闭(=true)后, 地磅读数照常显示, 但不自动填入表单, 需手动点"使用此读数"或手动输入.
// 默认 false(不禁用=保持自动跟随): 不改变现有烘干厂行为.
const SCALE_AUTO_FOLLOW_DISABLED_KEY = 'drying_scale_auto_follow_disabled_v1'

// ================== 读卡提示音开关 (机器级, 2026-09-27) ==================

// 读卡成功的"滴"声由前端在读到新证件号/新 NFC 卡号时调 /api/device/beep 主动触发
// （设备常驻静音，见 jinglunGo sdk.go OpenDevice）。此开关控制是否允许前端鸣响：
//   关 = 永不响（notifyBeep 直接短路，读到新卡的一切提示音全关）
//   开 = 仅读到新卡鸣一声（默认，现状行为）
// [2026-10-06] 写卡/擦卡成功不再鸣响（曾致一次绑卡响 3~4 声客诉），只剩识别新卡这一声
const READER_BEEP_ENABLED_KEY = 'drying_reader_beep_enabled_v1'

/** 读"读卡提示音"开关 (2026-09-28 用户拍板默认关：没存过返回 false = 静音) */
export function loadReaderBeepEnabled() {
  try {
    const raw = readScoped(READER_BEEP_ENABLED_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      return obj.enabled === true
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadReaderBeepEnabled 解析失败, 用默认 false:', e)
  }
  return false
}

/** 存"读卡提示音"开关 (即点即生效, 不参与设备配置页的未保存改动检测) */
export function saveReaderBeepEnabled(enabled) {
  localStorage.setItem(scopedKey(READER_BEEP_ENABLED_KEY), JSON.stringify({ enabled: !!enabled }))
}

// ================== 今日汇总显隐开关 (机器级) ==================

// 入库页右侧"今日汇总"统计面板是否显示.
// 默认 true(显示): 不改变现有烘干厂行为. 不需要的厂关掉即可腾出右侧空间.
const TODAY_SUMMARY_VISIBLE_KEY = 'drying_today_summary_visible_v1'

// ================== 本机无外设硬件总开关 (本机全局, 不按工厂隔离) ==================

// [2026-08-14] 场景: 财务/管理员用办公笔记本(不连地磅/读卡器/摄像头, 也没有好粮宝外设服务)
// 远程查看粮食入库/入库单列表, 进页会弹"好粮宝外设服务未启动"错误.
// 打开此开关后本机不再探测外设服务、不连地磅、不加载监控, 出入库页只查数据.
// 刻意不用 scopedKey 按工厂隔离: "本机有没有硬件"是机器属性而非工厂属性,
// 超管切厂查看时无需每个厂重设一次. 默认关闭(= 使用硬件), 烘干厂现场电脑行为不变.
const HARDWARE_DISABLED_KEY = 'drying_hardware_disabled_v1'

// ================== 监控功能开关 (机器级) ==================

// 是否启用过磅监控 (粮食入库/出库过磅页的监控窗口).
// 默认 true(启用): 不改变现有烘干厂行为. 有的烘干厂不装摄像头, 关掉后入库/出库监控窗口完全不渲染,
// 过磅设备配置页的录像机/摄像头通道区块也一并折叠隐藏, 配置页更清爽.
// 关闭后过磅抓拍自动走手动上传兜底 (scaleAndPhotoMixin.captureAndUpload 检测到监控组件未挂载即兜底).
const CAMERA_MONITOR_ENABLED_KEY = 'drying_camera_monitor_enabled_v1'

// ================== NFC 录单流程模式 (机器级) ==================

// NFC 录单有两种流程, 由本配置项切换, 默认原流程 (填完·放卡提交):
//   'swipe-after-fill'(默认, 原流程):  用户先填完表单, 再把 NFC 卡放上读卡器,
//     系统读到卡后立即自动识别并提交.
//   'swipe-then-remove'(备选, 新流程): 用户先把 NFC 卡放上读卡器 (卡持续在场),
//     填写完表单后取走卡片, 系统在卡离场瞬间自动提交并抓拍.
// 纯前端行为, 不同步后端. 默认 swipe-after-fill: 保持现有烘干厂行为不变.
const NFC_RECORD_FLOW_KEY = 'drying_nfc_record_flow_v1'
export const NFC_RECORD_FLOW_SWIPE_THEN_REMOVE = 'swipe-then-remove'
export const NFC_RECORD_FLOW_SWIPE_AFTER_FILL = 'swipe-after-fill'
export const NFC_RECORD_FLOW_OPTIONS = [
  { value: NFC_RECORD_FLOW_SWIPE_AFTER_FILL, label: '填完·放卡提交（默认）',
    desc: '先填完表单，再把 NFC 卡放上读卡器，自动识别并提交' },
  { value: NFC_RECORD_FLOW_SWIPE_THEN_REMOVE, label: '先放卡·取卡提交（备选）',
    desc: '先把 NFC 卡放上读卡器，填完表单后取走卡片即自动提交并抓拍' }
]

/** 读 NFC 录单流程模式 (没存过返回 'swipe-after-fill' 默认值) */
export function loadNfcRecordFlow() {
  try {
    const raw = readScoped(NFC_RECORD_FLOW_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      const v = obj.flow
      if (v === NFC_RECORD_FLOW_SWIPE_THEN_REMOVE || v === NFC_RECORD_FLOW_SWIPE_AFTER_FILL) return v
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadNfcRecordFlow 解析失败, 用默认 swipe-after-fill:', e)
  }
  return NFC_RECORD_FLOW_SWIPE_AFTER_FILL
}

/**
 * 存 NFC 录单流程模式. 只存 localStorage, 不同步后端.
 * @param {string} flow 'swipe-then-remove' | 'swipe-after-fill'
 */
export function saveNfcRecordFlow(flow) {
  const v = flow === NFC_RECORD_FLOW_SWIPE_THEN_REMOVE ? NFC_RECORD_FLOW_SWIPE_THEN_REMOVE : NFC_RECORD_FLOW_SWIPE_AFTER_FILL
  localStorage.setItem(scopedKey(NFC_RECORD_FLOW_KEY), JSON.stringify({ flow: v }))
  _markWeighConfigDirty(NFC_RECORD_FLOW_KEY)
}

/**
 * 读 NFC 轮询开关.
 * [2026-07-27] NFC 轮询固定开启, 恒返回 true (不再读 localStorage).
 * 保留函数签名兼容老调用方(EntryV1.vue / Exit.vue), 旧 localStorage 项已废弃.
 */
export function loadNfcPollingEnabled() {
  return true
}

/**
 * 存 NFC 轮询开关.
 * [2026-07-27] 已废弃, NFC 轮询固定开启不再可配置. 保留为空函数兼容老调用方.
 */
export function saveNfcPollingEnabled() {
  // no-op: NFC 轮询固定开启
}

/** 读"不自动跟随地磅读数"开关 (没存过返回 false = 保持自动跟随) */
export function loadScaleAutoFollowDisabled() {
  try {
    const raw = readScoped(SCALE_AUTO_FOLLOW_DISABLED_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      return !!obj.disabled
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadScaleAutoFollowDisabled 解析失败, 用默认 false:', e)
  }
  return false
}

/** 存"不自动跟随地磅读数"开关 */
export function saveScaleAutoFollowDisabled(disabled) {
  localStorage.setItem(scopedKey(SCALE_AUTO_FOLLOW_DISABLED_KEY), JSON.stringify({ disabled: !!disabled }))
  _markWeighConfigDirty(SCALE_AUTO_FOLLOW_DISABLED_KEY)
}

// ================== 地磅自动连接"已成功过一次"标记 (机器级) ==================
//
// [2026-07-25] 自动连接成功过一次后记下标记, 之后每次刷新页面的自动重连完全静默
// (不闪"连接中"状态、不弹"已连接"提示), 只在后台默默连好, 避免反复打扰用户.
// 首次成功前仍保留可见的连接反馈; 连接真的失败时顶栏会显示未连接+"连接"按钮, 用户可手动点.
const SCALE_AUTO_CONNECTED_ONCE_KEY = 'drying_scale_autonnected_once_v1'

/** 读某个磅 (entryScale/exitScale) 是否曾自动连接成功过 (没存过返回 false) */
export function loadScaleAutoConnectedOnce(scaleKey) {
  try {
    const raw = readScoped(SCALE_AUTO_CONNECTED_ONCE_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      return !!(obj && obj[scaleKey])
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadScaleAutoConnectedOnce 解析失败, 用默认 false:', e)
  }
  return false
}

/** 记下某个磅自动连接成功过一次 (之后的自动连接转静默) */
export function saveScaleAutoConnectedOnce(scaleKey) {
  let obj = {}
  try {
    const raw = readScoped(SCALE_AUTO_CONNECTED_ONCE_KEY)
    if (raw) obj = JSON.parse(raw) || {}
  } catch (e) { obj = {} }
  obj[scaleKey] = true
  localStorage.setItem(scopedKey(SCALE_AUTO_CONNECTED_ONCE_KEY), JSON.stringify(obj))
  _markWeighConfigDirty(SCALE_AUTO_CONNECTED_ONCE_KEY)
}

/** 读"今日汇总"显隐开关 (没存过返回 true = 显示) */
export function loadTodaySummaryVisible() {
  try {
    const raw = readScoped(TODAY_SUMMARY_VISIBLE_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      return obj.visible !== false
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadTodaySummaryVisible 解析失败, 用默认 true:', e)
  }
  return true
}

/** 存"今日汇总"显隐开关 */
export function saveTodaySummaryVisible(visible) {
  localStorage.setItem(scopedKey(TODAY_SUMMARY_VISIBLE_KEY), JSON.stringify({ visible: !!visible }))
  _markWeighConfigDirty(TODAY_SUMMARY_VISIBLE_KEY)
}

/** 读"监控功能"开关 (没存过返回 true = 启用) */
/**
 * loadCameraMode: hikvision | go2rtc (brand non-hikvision => go2rtc, default hikvision)
 */
export function loadCameraMode() {
  // provider: native(海康SDK) | go2rtc(通用)。老配置无 provider 时按 brand 推断。
  const cfg = loadCameraConfig()
  if (cfg.provider === 'go2rtc') return 'go2rtc'
  // 兼容：provider 缺失时，非海康品牌视为 go2rtc
  return (cfg.brand && cfg.brand !== 'hikvision') ? 'go2rtc' : 'native'
}

export function loadCameraMonitorEnabled() {
  try {
    const raw = readScoped(CAMERA_MONITOR_ENABLED_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      return obj.enabled !== false
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadCameraMonitorEnabled 解析失败, 用默认 true:', e)
  }
  return true
}

/** 存"监控功能"开关 */
export function saveCameraMonitorEnabled(enabled) {
  localStorage.setItem(scopedKey(CAMERA_MONITOR_ENABLED_KEY), JSON.stringify({ enabled: enabled !== false }))
  _markWeighConfigDirty(CAMERA_MONITOR_ENABLED_KEY)
}

/**
 * 读"本机无外设硬件"总开关 (没存过返回 false = 使用硬件).
 * 本机全局 key, 不按工厂隔离 (见 HARDWARE_DISABLED_KEY 注释).
 */
export function loadHardwareDisabled() {
  try {
    const raw = localStorage.getItem(HARDWARE_DISABLED_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      return !!obj.disabled
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadHardwareDisabled 解析失败, 用默认 false:', e)
  }
  return false
}

/** 存"本机无外设硬件"总开关 */
export function saveHardwareDisabled(disabled) {
  localStorage.setItem(HARDWARE_DISABLED_KEY, JSON.stringify({ disabled: !!disabled }))
  _markWeighConfigDirty(HARDWARE_DISABLED_KEY)
}

// ================== 针式打印机配置 (机器级) ==================

// 三联针式打印配置（按工厂隔离）
// 打印方式固定为浏览器原生打印（window.print），不再支持 ESC/P 命令打印。
const PRINTER_CONFIG_KEY = 'drying_printer_config_v1'

export const PRINT_MODE_BROWSER = 'browser'
// [已废弃] local 模式（ESC/P 命令打印）已移除，恒用 browser 模式
export const PRINT_MODE_OPTIONS = [
  { value: PRINT_MODE_BROWSER, label: '浏览器打印', desc: '用浏览器原生打印框打印三联单，无需本地服务（推荐）' }
]

// [2026-08 重构+回迁] 三联单据模板 — 新规模板版式来自《烘干厂出入库单据模版.xlsx》(settlementBillPrint.js),
//   另保留两套历史模板 (grainSettlementPrint.js / triplicateBrowserPrint.js) 作为可选项.
//   标签用现场话术: 新规=Excel 新版式, 老版=历史模板.
//   粮食入库单 4 选 1: simple=新规简版 | detail=新规详版 | grid=老版简版 | classic=老版详版;
//   粮食出库单 4 选 1: simple=新规简版 | detail=新规详版 | grid=老版简版 | classic=老版详版
//   [2026-08-25] 出库详版新增 (此前新规无出库详版, 版式对齐入库详版按出库口径裁剪);
//   农资入/出库固定用各自唯一新规模板 (老版模板不支持农资).
//   旧 billStyle 存量值 grid/classic 读取时直接还原为对应老版模板 (老用户无感).
export const BILL_TEMPLATE_SIMPLE = 'simple'
export const BILL_TEMPLATE_DETAIL = 'detail'
export const BILL_TEMPLATE_GRID = 'grid'
export const BILL_TEMPLATE_CLASSIC = 'classic'
const GRAIN_IN_TEMPLATE_VALUES = [BILL_TEMPLATE_SIMPLE, BILL_TEMPLATE_DETAIL, BILL_TEMPLATE_GRID, BILL_TEMPLATE_CLASSIC]
const GRAIN_OUT_TEMPLATE_VALUES = [BILL_TEMPLATE_SIMPLE, BILL_TEMPLATE_DETAIL, BILL_TEMPLATE_GRID, BILL_TEMPLATE_CLASSIC]
export const BILL_TEMPLATE_OPTIONS = [
  { value: BILL_TEMPLATE_SIMPLE, label: '新规简版', desc: '横向表格：品类/品种/水分/毛重/皮重/扣重/结算重量/结算单价/结算金额' },
  { value: BILL_TEMPLATE_DETAIL, label: '新规详版', desc: '竖排信息表：客商/身份证(可开关)/银行卡/司机/质检/扣价/结算等全字段' },
  { value: BILL_TEMPLATE_GRID, label: '老版简版', desc: '历史横向表格模板（原「表格样式」）：品类/毛重/皮重/杂质/扣重/净重/价格/金额 + 卸装费/合计行' },
  { value: BILL_TEMPLATE_CLASSIC, label: '老版详版', desc: '历史键值对模板（原「经典样式」）：纵向字段表格，信息最全（含单位/质价扣款/司机等）' }
]
// [2026-08-25] 出库模板同入库四选一; 详版 desc 换成出库字段口径 (出库详版无身份证/银行卡栏)
export const OUT_BILL_TEMPLATE_OPTIONS = BILL_TEMPLATE_OPTIONS.map(o => o.value === BILL_TEMPLATE_DETAIL
  ? { ...o, desc: '竖排信息表：客商/出库类型/仓号/司机/品种/水分/重量/结算金额等全字段' }
  : o)
const DEFAULT_BILL_TEMPLATE = BILL_TEMPLATE_SIMPLE

// [2026-08-22] 「保存并打印」输出单据 — 入库回皮抽屉/出库称毛抽屉的"保存并打印"按钮打哪种单:
//   triplicate = 三联结算单(针式, 默认, 既有行为) | receipt = 热敏小票(58/80mm, 纸宽用 receiptPaperWidth).
//   只影响这两个抽屉的"保存并打印"; 列表/详情里的"三联打印""打印小票"菜单不受影响.
export const SAVE_PRINT_MODE_TRIPPLICATE = 'triplicate'
export const SAVE_PRINT_MODE_RECEIPT = 'receipt'
export const SAVE_PRINT_MODE_OPTIONS = [
  { value: SAVE_PRINT_MODE_TRIPPLICATE, label: '三联单（针式）', desc: '「保存并打印」打印三联结算单（默认，现状行为）' },
  { value: SAVE_PRINT_MODE_RECEIPT, label: '小票（热敏）', desc: '「保存并打印」打印热敏小票（纸宽在「小票打印」分区设置）' }
]
const DEFAULT_SAVE_PRINT_MODE = SAVE_PRINT_MODE_TRIPPLICATE
/** 规范化「保存并打印」输出: 仅接受 receipt, 其余一律回退 triplicate(默认) */
function sanitizeSavePrintMode(v) {
  return v === SAVE_PRINT_MODE_RECEIPT ? SAVE_PRINT_MODE_RECEIPT : SAVE_PRINT_MODE_TRIPPLICATE
}
const DEFAULT_DETAIL_SHOW_ID_CARD = true    // [新规详版] 身份证号码默认打印, 配置可关
const DEFAULT_SIMPLE_SHOW_ID_CARD = false   // [新规简版] 身份证号码行默认不打印 (简版历史上无此栏), 配置可开
const DEFAULT_SIMPLE_SHOW_QP_DEDUCTION = false // [新规简版] 质价扣款注记行默认不打印 (2026-10-06 客诉: 农户看到扣款金额计较; 无人要求显示), 配置可开
const DEFAULT_BILL_PREPARER = ''             // [制单员] 打印时的制单员显示名 (空=用建单人 createBy; 建单账号常是公司名, 现场要求可改)
const DEFAULT_BILL_SHOW_FOOT_NOTE = true       // [底部警示语] 入库注意行/出库警示行/老版简版注意行, 默认打印
// [2026-10-12 注意行自定义] 粮食入库单底部注意行文案 (新规简版/详版 + 老版 grid 通用; 空串=用默认文案).
//   客户反馈「唯一凭证」这句写死了想改 → 做成可配置; 出库新规模板底部是超载警示行, 不随此文案改.
export const DEFAULT_BILL_FOOT_NOTE_TEXT = '注意:此单据为证明您向我站点交粮的唯一凭证，请妥善保管。'
// [通用配置] 字体放大/加粗 — 三联单新旧模板共用 (新规四模板 + 老版 grid/classic 全部生效).
//   放大以各模板基准字号为 1.0 (新规=正文11pt/标题16pt; 老版=各自基准), 只缩放字号不改版式;
//   加粗提升针打复写穿透力. 纸张/页边距仍交浏览器打印对话框.
const BILL_FONT_SCALE_MIN = 0.5
const BILL_FONT_SCALE_MAX = 2.0
const DEFAULT_BILL_FONT_SCALE = 1.0
const DEFAULT_BILL_BOLD = false
/** 规范化字体放大倍率: 数值有效则夹在 0.5~2.0, 否则回退 1.0(默认) */
function sanitizeBillFontScale(v) {
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_BILL_FONT_SCALE
  return Math.min(BILL_FONT_SCALE_MAX, Math.max(BILL_FONT_SCALE_MIN, Math.round(n * 100) / 100))
}

// [2026-10-12 字号/加粗分单据类型] 四种单据各存各的键 (billFontScaleGrainIn 等 8 键);
//   存量配置只有旧通用键 billFontScale/billBold 时读取兜底迁移到四键, 一经保存即以分键为准.
function pickPerBillFontScale(obj, key) {
  const v = Number(obj && obj[key])
  if (Number.isFinite(v) && v > 0) return sanitizeBillFontScale(v)
  return sanitizeBillFontScale(obj && obj.billFontScale)
}
function pickPerBillBold(obj, key) {
  const v = obj && obj[key]
  if (typeof v === 'boolean') return v
  return (obj && obj.billBold) === true
}
// [专有配置] 签字行打印司机姓名 — 新规粮食模板签字行「司机：___」印 driverNameSnap, 默认开 (2026-08-22 现场反馈).
//   关闭后留空白手签. 农资单据无司机字段不受影响; 老版 classic 司机在正文栏, 老版 grid 无司机栏.
const DEFAULT_BILL_SIGN_DRIVER = true

// [2026-09-15 客商身份证/电话选配] 三联单客商电话号码开关 — 客商名称后括号里的手机号
//   (粮食/农资出入库新旧模板通用, 出库详版「客商联系方式」栏同开关). 默认开=现状行为.
const DEFAULT_BILL_SHOW_PHONE = true

// [2026-09-27 入库单号选配] 三联单「入库单号」开关 — 入库方向单据(粮食入库简版/详版/农资入库/
//   老版 grid 入库/classic 入库)顶部信息行的入库单号字段, 默认开=现状行为. 只管入库, 出库单号不受控.
const DEFAULT_BILL_SHOW_INBOUND_NO = true

// [2026-09-15 小票选配字段] 热敏小票 身份证/电话/银行卡/备注 四个开关.
//   身份证/电话/银行卡是小票新内容, 默认不打印 (选配开启才印; 取单据客商快照, 无数据自动不印;
//   出库单据无客商证件快照字段, 身份证开关实际只对入库小票生效; 银行卡入库取农户快照、出库取 customerBankAccount).
//   备注是既有内容 (有备注就印), [2026-09-16] 默认改为不打印, 与其余选配字段统一 (勾选才印).
const DEFAULT_RECEIPT_SHOW_ID_CARD = false
const DEFAULT_RECEIPT_SHOW_PHONE = false
const DEFAULT_RECEIPT_SHOW_BANK_CARD = false
const DEFAULT_RECEIPT_SHOW_REMARK = false

// [2026-09-15 二维码自定义大小] 热敏小票二维码边长 (mm), 按纸宽分别记忆.
//   58mm 纸可打宽度约 48mm, 上限留白取 44; 80mm 纸内容宽 76mm, 同上限. 下限 16 保证可扫.
const RECEIPT_QR_SIZE_MIN = 16
const RECEIPT_QR_SIZE_MAX = 44
const DEFAULT_RECEIPT_QR_SIZE_58 = 34
const DEFAULT_RECEIPT_QR_SIZE_80 = 28

/** 规范化二维码边长: 整数夹在 16~44, 非法回退 fallback */
function sanitizeReceiptQrSize(v, fallback) {
  const n = Math.round(Number(v))
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.min(RECEIPT_QR_SIZE_MAX, Math.max(RECEIPT_QR_SIZE_MIN, n))
}

/** 规范化粮食入库模板: 4 选 1; 旧 billStyle 值 grid/classic 直接还原为对应旧模板 */
function sanitizeBillTemplate(v, legacyStyle) {
  if (GRAIN_IN_TEMPLATE_VALUES.includes(v)) return v
  if (legacyStyle === BILL_TEMPLATE_GRID || legacyStyle === BILL_TEMPLATE_CLASSIC) return legacyStyle
  return DEFAULT_BILL_TEMPLATE
}

/** 规范化粮食出库模板: 4 选 1; 未存过时跟随旧 billStyle (旧开关当年入出库一起切) */
function sanitizeOutBillTemplate(v, legacyStyle) {
  if (GRAIN_OUT_TEMPLATE_VALUES.includes(v)) return v
  if (legacyStyle === BILL_TEMPLATE_GRID || legacyStyle === BILL_TEMPLATE_CLASSIC) return legacyStyle
  return DEFAULT_BILL_TEMPLATE
}

// [小票纸张宽度] 热敏小票(入库回皮凭证/入库结算/出库小票)的卷纸宽度档位.
//   58 = 58mm 窄卷纸(默认, 现有布局)
//   80 = 80mm 宽卷纸(重量三列横排, 二维码居中、备注独立行, 独立排版)
// 与已废弃的 paperWidth(ESC/P 针打列宽) 无关, 单独存储. 默认 58 保持老用户无感.
export const RECEIPT_PAPER_WIDTH_58 = 58
export const RECEIPT_PAPER_WIDTH_80 = 80
export const RECEIPT_PAPER_WIDTH_OPTIONS = [
  { value: RECEIPT_PAPER_WIDTH_58, label: '58mm', desc: '常见窄卷纸热敏打印机（默认）' },
  { value: RECEIPT_PAPER_WIDTH_80, label: '80mm', desc: '宽卷纸，字段更宽松、重量三列横排、二维码居中、备注独立行' },
]
const DEFAULT_RECEIPT_PAPER_WIDTH = RECEIPT_PAPER_WIDTH_58
/** 规范化小票纸张宽度: 仅接受 80, 其余一律回退 58(默认) */
function sanitizeReceiptPaperWidth(v) {
  return Number(v) === RECEIPT_PAPER_WIDTH_80 ? RECEIPT_PAPER_WIDTH_80 : RECEIPT_PAPER_WIDTH_58
}

// [小票模板] 热敏小票排版模板 (入库回皮凭证/入库结算/出库小票统一生效), 58/80 纸宽各自适配.
//   standard = 标准版(默认, 既有表格排版) | large = 大字版(字体放大+纵向信息流排版,
//   解决部分用户热敏打印机字体过小看不清的问题).
export const RECEIPT_TEMPLATE_STANDARD = 'standard'
export const RECEIPT_TEMPLATE_LARGE = 'large'
export const RECEIPT_TEMPLATE_OPTIONS = [
  { value: RECEIPT_TEMPLATE_STANDARD, label: '标准版', desc: '表格排版：信息两列 + 重量分栏 + 居中二维码（默认）' },
  { value: RECEIPT_TEMPLATE_LARGE, label: '大字版', desc: '字体整体放大、纵向信息流排版，适合打印出来看不清的场景' },
]
const DEFAULT_RECEIPT_TEMPLATE = RECEIPT_TEMPLATE_STANDARD
/** 规范化小票模板: 仅接受 large, 其余回退 standard(默认) */
function sanitizeReceiptTemplate(v) {
  return v === RECEIPT_TEMPLATE_LARGE ? RECEIPT_TEMPLATE_LARGE : RECEIPT_TEMPLATE_STANDARD
}

// [大字版专属配置] 仅 receiptTemplate=large 时生效:
//   receiptHeader    烘干厂抬头 (标题字体打印, 空串=用当前烘干厂名)
//   receiptShowTime  是否打印时间行 true(默认; 关掉只留日期行)
//   receiptWarningOn 是否打印警示语 true(默认)
//   receiptWarningText 自定义警示语 (空=用默认警示语)
//   receiptAddress / receiptPhone 烘干厂自填地址/电话, 只在大字版小票底部打印 (空则不打印)
export const DEFAULT_RECEIPT_WARNING_TEXT = '此单据为结算唯一凭证，请妥善保管'

// [2026-07-27] 纸张尺寸不再由前端配置 — 改由浏览器打印对话框选择纸张/调整大小.
// 旧版 BROWSER_PAPER_WIDTH_OPTIONS / BROWSER_PAPER_HEIGHT_OPTIONS 及 browserPaperWidth/Height 字段已移除.
// [2026-08 重构] 字号/加粗/四向页边距/列数/联次标注/称谓等三联打印配置项已全部移除,
//   统一交给浏览器打印对话框调试 (自由度更高); 仅保留 三联抬头(billHeader) 与 单据模板(billTemplate).

// [出库隐藏金额] 粮食出库(备车出库+客户自提)小票/二维码不打印应收金额.
// 默认关 (false=照常打印金额). 只影响小票打印与二维码, 不影响屏幕显示和单据数据; 只对出库生效.
const DEFAULT_HIDE_OUTBOUND_AMOUNT = false

/**
 * 读针式打印机配置 (通用配置 + 专有配置, 2026-08-22 分组).
 * @returns {{printMode:string, billTemplate:string, outBillTemplate:string, billHeader:string,
 *            detailShowIdCard:boolean, simpleShowIdCard:boolean, billPreparer:string,
 *            billShowFootNote:boolean, billFontScaleGrainIn:number, billBoldGrainIn:boolean, billSignDriver:boolean,
 *            billShowPhone:boolean, billShowInboundNo:boolean,
 *            browserHideAmountSelfPickup:boolean, browserHideAmountFactoryDeliver:boolean,
 *            receiptPaperWidth:number, savePrintMode:string,
 *            receiptShowIdCard:boolean, receiptShowPhone:boolean, receiptShowBankCard:boolean, receiptShowRemark:boolean}}
 *         printMode 恒 'browser' (ESC/P 已移除)
 *         —— 通用配置 (三联单新旧模板共用) ——
 *         billHeader 三联抬头 (替换结算单标题里的单位名前缀, 空串=用烘干厂名)
 *         billTitleGrainIn/billTitleGrainOut/billTitleInputIn/billTitleInputOut 单据大标题字样 (四种单据各一字段, 随便改;
 *                                     留空=默认历史字样「粮食入库结算单」等; 粮食/农资 × 入/出库的新旧模板共用)
 *         billPreparer 制单员打印显示名 (空串=用建单人 createBy)
 *         billShowFootNote 是否打印底部警示语 true(默认; 入库注意行/出库警示行/老版简版注意行)
 *         billFootNoteText 底部注意行自定义文案 (粮食入库单注意行, 空串=默认「注意:此单据为…妥善保管。」; 出库超载警示行不受控)
 *         billFontScaleGrainIn/billFontScaleGrainOut/billFontScaleInputIn/billFontScaleInputOut 字体放大倍率 [2026-10-12 分单据类型]
 *                                     1.0(默认), 0.5~2.0 夹取 (只缩放字号, 版式不动; 四种单据各存各的, 旧通用键 billFontScale 读取时兜底迁移)
 *         billBoldGrainIn/billBoldGrainOut/billBoldInputIn/billBoldInputOut 正文是否加粗 false(默认; 针打复写穿透力更强;
 *                                     四种单据各存各的, 旧通用键 billBold 读取时兜底迁移)
 *         billShowPhone 是否打印客商电话号码 true(默认; 客商名后括号手机号, 新旧模板通用)
 *         billShowInboundNo 是否打印入库单号 true(默认; 入库方向单据顶部信息行, 出库单号不受控)
 *         —— 专有配置 (按所选模板生效) ——
 *         billTemplate 粮食入库单模板 'simple'(新规简版,默认)|'detail'(新规详版)|'grid'(老版简版)|'classic'(老版详版)
 *         outBillTemplate 粮食出库单模板 'simple'(默认)|'detail'|'grid'|'classic'
 *         旧 billStyle 存量值 grid/classic 读取时直接还原为对应老版模板
 *         detailShowIdCard 新规详版是否打印身份证号码 true(默认)
 *         simpleShowIdCard 新规简版是否打印身份证号码行 false(默认)
 *         billSignDriver 新规粮食模板签字行是否印司机姓名 true(默认; 关掉留空白手签)
 *         —— 小票/输出路由 ——
 *         browserHideAmountSelfPickup 客户自提小票是否隐藏金额 false(默认, 仅出库生效)
 *         browserHideAmountFactoryDeliver 备车出库小票是否隐藏金额 false(默认)
 *         receiptPaperWidth 热敏小票纸宽 58(默认)|80
 *         receiptTemplate 热敏小票模板 'standard'(标准版,默认)|'large'(大字版)
 *         receiptHeader 大字版抬头 (空串=用烘干厂名)
 *         receiptShowTime 大字版是否打印时间行 true(默认)
 *         receiptWarningOn 大字版是否打印警示语 true(默认)
 *         receiptWarningText 大字版自定义警示语 (空串=用默认警示语)
 *         receiptAddress / receiptPhone 大字版底部地址/电话 (烘干厂自填, 空则不打印)
 *         receiptShowIdCard 小票是否打印身份证 false(默认; 入库客商快照, 无数据不印)
 *         receiptShowPhone 小票是否打印电话号码 false(默认; 客商快照电话)
 *         receiptShowBankCard 小票是否打印银行卡号 false(默认; 入库农户快照/出库 customerBankAccount)
 *         receiptShowRemark 小票是否打印备注 false(默认; [2026-09-16] 改为选配, 勾选才印业务备注行)
 *         receiptQrSizeMm58 / receiptQrSizeMm80 二维码边长 mm (按纸宽记忆, 16~44, 默认 34/28)
 *         savePrintMode 回皮/称毛抽屉「保存并打印」输出 'triplicate'(三联,默认)|'receipt'(热敏小票)
 */
export function loadPrinterConfig() {
  try {
    const raw = readScoped(PRINTER_CONFIG_KEY)
    if (raw) {
      const obj = JSON.parse(raw)
      return {
        printMode: PRINT_MODE_BROWSER,
        billTemplate: sanitizeBillTemplate(obj.billTemplate, obj.billStyle),
        outBillTemplate: sanitizeOutBillTemplate(obj.outBillTemplate, obj.billStyle),
        billHeader: typeof obj.billHeader === 'string' ? obj.billHeader : '',
        billTitleGrainIn: typeof obj.billTitleGrainIn === 'string' ? obj.billTitleGrainIn : '',
        billTitleGrainOut: typeof obj.billTitleGrainOut === 'string' ? obj.billTitleGrainOut : '',
        billTitleInputIn: typeof obj.billTitleInputIn === 'string' ? obj.billTitleInputIn : '',
        billTitleInputOut: typeof obj.billTitleInputOut === 'string' ? obj.billTitleInputOut : '',   // 标题字样留空='' → 打印层回默认历史字样
        detailShowIdCard: obj.detailShowIdCard !== false,
        simpleShowIdCard: obj.simpleShowIdCard === true,
        simpleShowQpDeduction: obj.simpleShowQpDeduction === true,
        billPreparer: typeof obj.billPreparer === 'string' ? obj.billPreparer : '',
        billShowFootNote: obj.billShowFootNote !== false,
        billFootNoteText: typeof obj.billFootNoteText === 'string' ? obj.billFootNoteText : '',   // 注意行留空='' → 打印层回默认文案
        billFontScaleGrainIn: pickPerBillFontScale(obj, 'billFontScaleGrainIn'),       // [分单据类型 2026-10-12] 粮食入库字体放大 (旧通用键兜底迁移)
        billFontScaleGrainOut: pickPerBillFontScale(obj, 'billFontScaleGrainOut'),     // 粮食出库字体放大
        billFontScaleInputIn: pickPerBillFontScale(obj, 'billFontScaleInputIn'),       // 农资入库字体放大
        billFontScaleInputOut: pickPerBillFontScale(obj, 'billFontScaleInputOut'),     // 农资出库字体放大
        billBoldGrainIn: pickPerBillBold(obj, 'billBoldGrainIn'),                      // [分单据类型 2026-10-12] 粮食入库加粗 (旧通用键兜底迁移)
        billBoldGrainOut: pickPerBillBold(obj, 'billBoldGrainOut'),                    // 粮食出库加粗
        billBoldInputIn: pickPerBillBold(obj, 'billBoldInputIn'),                      // 农资入库加粗
        billBoldInputOut: pickPerBillBold(obj, 'billBoldInputOut'),                    // 农资出库加粗
        billSignDriver: obj.billSignDriver !== false,
        billShowPhone: obj.billShowPhone !== false,
        billShowInboundNo: obj.billShowInboundNo !== false,
        // [出库隐藏金额] 按出库类型独立控制 (客户自提/备车出库各一个开关), 作用于小票/二维码.
        // 向后兼容: 旧版单开关 browserHideOutboundAmount 存在且新字段未显式设置时, 两个开关继承旧值.
        browserHideAmountSelfPickup: typeof obj.browserHideAmountSelfPickup === 'boolean' ? obj.browserHideAmountSelfPickup : (obj.browserHideOutboundAmount === true),
        browserHideAmountFactoryDeliver: typeof obj.browserHideAmountFactoryDeliver === 'boolean' ? obj.browserHideAmountFactoryDeliver : (obj.browserHideOutboundAmount === true),
        receiptPaperWidth: sanitizeReceiptPaperWidth(obj.receiptPaperWidth),
        receiptTemplate: sanitizeReceiptTemplate(obj.receiptTemplate),
        receiptHeader: typeof obj.receiptHeader === 'string' ? obj.receiptHeader : '',
        receiptShowTime: obj.receiptShowTime !== false,
        receiptWarningOn: obj.receiptWarningOn !== false,
        receiptWarningText: typeof obj.receiptWarningText === 'string' ? obj.receiptWarningText : '',
        receiptAddress: typeof obj.receiptAddress === 'string' ? obj.receiptAddress : '',
        receiptPhone: typeof obj.receiptPhone === 'string' ? obj.receiptPhone : '',
        receiptShowIdCard: obj.receiptShowIdCard === true,
        receiptShowPhone: obj.receiptShowPhone === true,
        receiptShowBankCard: obj.receiptShowBankCard === true,
        receiptShowRemark: obj.receiptShowRemark === true,
        receiptQrSizeMm58: sanitizeReceiptQrSize(obj.receiptQrSizeMm58, DEFAULT_RECEIPT_QR_SIZE_58),
        receiptQrSizeMm80: sanitizeReceiptQrSize(obj.receiptQrSizeMm80, DEFAULT_RECEIPT_QR_SIZE_80),
        savePrintMode: sanitizeSavePrintMode(obj.savePrintMode)
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[weighDevice] loadPrinterConfig 解析失败, 用默认:', e)
  }
  return {
    printMode: PRINT_MODE_BROWSER,
    billTemplate: DEFAULT_BILL_TEMPLATE,
    outBillTemplate: DEFAULT_BILL_TEMPLATE,
    billHeader: '',
    billTitleGrainIn: '',
    billTitleGrainOut: '',
    billTitleInputIn: '',
    billTitleInputOut: '',
    detailShowIdCard: DEFAULT_DETAIL_SHOW_ID_CARD,
    simpleShowIdCard: DEFAULT_SIMPLE_SHOW_ID_CARD,
    simpleShowQpDeduction: DEFAULT_SIMPLE_SHOW_QP_DEDUCTION,
    billPreparer: DEFAULT_BILL_PREPARER,
    billShowFootNote: DEFAULT_BILL_SHOW_FOOT_NOTE,
    billFootNoteText: '',
    billFontScaleGrainIn: DEFAULT_BILL_FONT_SCALE,
    billFontScaleGrainOut: DEFAULT_BILL_FONT_SCALE,
    billFontScaleInputIn: DEFAULT_BILL_FONT_SCALE,
    billFontScaleInputOut: DEFAULT_BILL_FONT_SCALE,
    billBoldGrainIn: DEFAULT_BILL_BOLD,
    billBoldGrainOut: DEFAULT_BILL_BOLD,
    billBoldInputIn: DEFAULT_BILL_BOLD,
    billBoldInputOut: DEFAULT_BILL_BOLD,
    billSignDriver: DEFAULT_BILL_SIGN_DRIVER,
    billShowPhone: DEFAULT_BILL_SHOW_PHONE,
    billShowInboundNo: DEFAULT_BILL_SHOW_INBOUND_NO,
    browserHideAmountSelfPickup: DEFAULT_HIDE_OUTBOUND_AMOUNT,
    browserHideAmountFactoryDeliver: DEFAULT_HIDE_OUTBOUND_AMOUNT,
    receiptPaperWidth: DEFAULT_RECEIPT_PAPER_WIDTH,
    receiptTemplate: DEFAULT_RECEIPT_TEMPLATE,
    receiptHeader: '',
    receiptShowTime: true,
    receiptWarningOn: true,
    receiptWarningText: '',
    receiptAddress: '',
    receiptPhone: '',
    receiptShowIdCard: DEFAULT_RECEIPT_SHOW_ID_CARD,
    receiptShowPhone: DEFAULT_RECEIPT_SHOW_PHONE,
    receiptShowBankCard: DEFAULT_RECEIPT_SHOW_BANK_CARD,
    receiptShowRemark: DEFAULT_RECEIPT_SHOW_REMARK,
    receiptQrSizeMm58: DEFAULT_RECEIPT_QR_SIZE_58,
    receiptQrSizeMm80: DEFAULT_RECEIPT_QR_SIZE_80,
    savePrintMode: DEFAULT_SAVE_PRINT_MODE
  }
}

/** 存针式打印机配置 */
export function savePrinterConfig(cfg) {
  localStorage.setItem(scopedKey(PRINTER_CONFIG_KEY), JSON.stringify({
    printMode: PRINT_MODE_BROWSER,
    billTemplate: sanitizeBillTemplate(cfg && cfg.billTemplate, cfg && cfg.billStyle),
    outBillTemplate: sanitizeOutBillTemplate(cfg && cfg.outBillTemplate, cfg && cfg.billStyle),
    billHeader: String((cfg && cfg.billHeader) || ''),   // 三联抬头 (空串=用烘干厂名)
    billTitleGrainIn: String((cfg && cfg.billTitleGrainIn) || '').trim(),     // 粮食入库标题字样 (空=默认「粮食入库结算单」)
    billTitleGrainOut: String((cfg && cfg.billTitleGrainOut) || '').trim(),   // 粮食出库标题字样 (空=默认「粮食出库结算单」)
    billTitleInputIn: String((cfg && cfg.billTitleInputIn) || '').trim(),     // 农资入库标题字样 (空=默认「农资入库结算单」)
    billTitleInputOut: String((cfg && cfg.billTitleInputOut) || '').trim(),   // 农资出库标题字样 (空=默认「农资出库结算单」)
    detailShowIdCard: (cfg && cfg.detailShowIdCard) !== false,   // 新规详版打印身份证 (默认开)
    simpleShowIdCard: (cfg && cfg.simpleShowIdCard) === true,     // 新规简版打印身份证行 (默认关)
    simpleShowQpDeduction: (cfg && cfg.simpleShowQpDeduction) === true, // 新规简版打印质价扣款注记行 (默认关, [2026-10-06])
    billPreparer: String((cfg && cfg.billPreparer) || '').trim(), // 制单员打印显示名 (空=用建单人)
    billShowFootNote: (cfg && cfg.billShowFootNote) !== false,   // 底部警示语 (默认打印)
    billFootNoteText: String((cfg && cfg.billFootNoteText) || '').trim(), // 注意行自定义文案 (空=默认文案)
    billFontScaleGrainIn: sanitizeBillFontScale(cfg && cfg.billFontScaleGrainIn),   // [分单据类型 2026-10-12] 粮食入库字体放大 (默认 1.0)
    billFontScaleGrainOut: sanitizeBillFontScale(cfg && cfg.billFontScaleGrainOut), // 粮食出库字体放大
    billFontScaleInputIn: sanitizeBillFontScale(cfg && cfg.billFontScaleInputIn),   // 农资入库字体放大
    billFontScaleInputOut: sanitizeBillFontScale(cfg && cfg.billFontScaleInputOut), // 农资出库字体放大
    billBoldGrainIn: (cfg && cfg.billBoldGrainIn) === true,                         // [分单据类型 2026-10-12] 粮食入库加粗 (默认关)
    billBoldGrainOut: (cfg && cfg.billBoldGrainOut) === true,                       // 粮食出库加粗
    billBoldInputIn: (cfg && cfg.billBoldInputIn) === true,                         // 农资入库加粗
    billBoldInputOut: (cfg && cfg.billBoldInputOut) === true,                       // 农资出库加粗
    billSignDriver: (cfg && cfg.billSignDriver) !== false,           // [专有] 签字行印司机姓名 (默认开)
    billShowPhone: (cfg && cfg.billShowPhone) !== false,             // [通用] 客商电话号码 (默认开)
    billShowInboundNo: (cfg && cfg.billShowInboundNo) !== false,     // [通用] 入库单号 (默认开, 出库不受控)
    browserHideAmountSelfPickup: (cfg && cfg.browserHideAmountSelfPickup) === true,      // 客户自提隐藏金额 (默认关)
    browserHideAmountFactoryDeliver: (cfg && cfg.browserHideAmountFactoryDeliver) === true, // 备车出库隐藏金额 (默认关)
    receiptPaperWidth: sanitizeReceiptPaperWidth(cfg && cfg.receiptPaperWidth),
    receiptTemplate: sanitizeReceiptTemplate(cfg && cfg.receiptTemplate),
    receiptHeader: String((cfg && cfg.receiptHeader) || '').trim(),              // 大字版抬头 (空=用烘干厂名)
    receiptShowTime: (cfg && cfg.receiptShowTime) !== false,                     // 大字版时间行 (默认开)
    receiptWarningOn: (cfg && cfg.receiptWarningOn) !== false,                   // 大字版警示语 (默认开)
    receiptWarningText: String((cfg && cfg.receiptWarningText) || '').trim(),    // 自定义警示语 (空=默认文案)
    receiptAddress: String((cfg && cfg.receiptAddress) || '').trim(),            // 大字版地址 (烘干厂自填)
    receiptPhone: String((cfg && cfg.receiptPhone) || '').trim(),                // 大字版电话 (烘干厂自填)
    receiptShowIdCard: (cfg && cfg.receiptShowIdCard) === true,                  // 小票身份证 (默认关)
    receiptShowPhone: (cfg && cfg.receiptShowPhone) === true,                    // 小票电话号码 (默认关)
    receiptShowBankCard: (cfg && cfg.receiptShowBankCard) === true,              // 小票银行卡号 (默认关)
    receiptShowRemark: (cfg && cfg.receiptShowRemark) === true,                  // 小票备注 (默认关, [2026-09-16] 改选配)
    receiptQrSizeMm58: sanitizeReceiptQrSize(cfg && cfg.receiptQrSizeMm58, DEFAULT_RECEIPT_QR_SIZE_58),  // 二维码边长 58mm 纸 (默认 34)
    receiptQrSizeMm80: sanitizeReceiptQrSize(cfg && cfg.receiptQrSizeMm80, DEFAULT_RECEIPT_QR_SIZE_80),  // 二维码边长 80mm 纸 (默认 28)
    savePrintMode: sanitizeSavePrintMode(cfg && cfg.savePrintMode)   // 「保存并打印」输出: 三联(默认)|小票
  }))
  _markWeighConfigDirty(PRINTER_CONFIG_KEY)
}

/** 便捷：读打印方式（恒返回 'browser'，ESC/P 命令打印已移除） */
export function loadPrintMode() {
  return PRINT_MODE_BROWSER
}

/** 便捷：读粮食入库单模板 (默认 'simple' 简版): simple|detail|grid|classic */
export function loadBillTemplate() {
  return loadPrinterConfig().billTemplate
}

/** 便捷：读粮食出库单模板 (默认 'simple' 简版): simple|detail|grid|classic */
export function loadOutboundBillTemplate() {
  return loadPrinterConfig().outBillTemplate
}

/**
 * 便捷：读热敏小票纸张宽度 (默认 58).
 * @returns {number} 58 | 80. 入库回皮凭证/入库结算/出库小票统一读取此值选 58mm 或 80mm 排版.
 */
export function loadReceiptPaperWidth() {
  return loadPrinterConfig().receiptPaperWidth
}

/**
 * 便捷：读热敏小票模板 (默认 'standard' 标准版).
 * @returns {string} 'standard' 标准版 | 'large' 大字版. 入库回皮凭证/入库结算/出库小票统一读取此值选排版.
 */
export function loadReceiptTemplate() {
  return loadPrinterConfig().receiptTemplate
}

/**
 * 便捷：读大字版小票专属配置 (仅 receiptTemplate=large 时生效).
 * @returns {{header:string, showTime:boolean, warningOn:boolean, warningText:string, address:string, phone:string}}
 *          warningText 已回退默认警示语 (未配置时返回 DEFAULT_RECEIPT_WARNING_TEXT).
 */
export function loadReceiptLargeConfig() {
  const cfg = loadPrinterConfig()
  return {
    header: cfg.receiptHeader,
    showTime: cfg.receiptShowTime,
    warningOn: cfg.receiptWarningOn,
    warningText: cfg.receiptWarningText || DEFAULT_RECEIPT_WARNING_TEXT,
    address: cfg.receiptAddress,
    phone: cfg.receiptPhone
  }
}

/**
 * 便捷：读小票选配字段开关 (身份证/电话/银行卡/备注).
 * @returns {{showIdCard:boolean, showPhone:boolean, showBankCard:boolean, showRemark:boolean}}
 *          四项全部默认 false (选配开启才印; 单据无对应数据时模板自动不渲染该行).
 *          入库回皮凭证/入库结算/出库小票统一读取.
 */
export function loadReceiptPrintFields() {
  const cfg = loadPrinterConfig()
  return {
    showIdCard: cfg.receiptShowIdCard === true,
    showPhone: cfg.receiptShowPhone === true,
    showBankCard: cfg.receiptShowBankCard === true,
    showRemark: cfg.receiptShowRemark === true
  }
}

/**
 * 便捷：读当前纸宽下的二维码边长 (mm). 按纸宽分别记忆 (58 默认 34 / 80 默认 28).
 * @param {number|string} [paperWidth] 卷纸宽度 58|80, 其他值按 58 档取
 * @returns {number} 边长 mm, 16~44
 */
export function loadReceiptQrSize(paperWidth) {
  const cfg = loadPrinterConfig()
  return Number(paperWidth) === RECEIPT_PAPER_WIDTH_80
    ? cfg.receiptQrSizeMm80
    : cfg.receiptQrSizeMm58
}

/**
 * 便捷：读回皮/称毛抽屉「保存并打印」输出单据 (默认 'triplicate').
 * @returns {string} 'triplicate' 三联结算单(针式) | 'receipt' 热敏小票(58/80mm).
 * 入库回皮抽屉、出库称毛抽屉的「保存并打印」按此路由打印; 列表/详情的显式打印菜单不受影响.
 */
export function loadSavePrintMode() {
  return loadPrinterConfig().savePrintMode
}

/**
 * 便捷：按出库类型读打印是否隐藏金额 (默认 false).
 * @param {string} [deliveryType] 'self_pickup'(客户自提, 默认) | 'factory_deliver'(备车出库)
 * 仅对粮食出库生效: 开启后对应类型的小票不打印应收金额, 二维码不含amt.
 * 单价始终打印. 入库不受影响. 只影响打印, 不影响屏幕显示和单据数据.
 */
export function loadHideOutboundAmount(deliveryType) {
  const cfg = loadPrinterConfig()
  return deliveryType === 'factory_deliver'
    ? cfg.browserHideAmountFactoryDeliver === true
    : cfg.browserHideAmountSelfPickup === true
}

// ================== [2026-08-17] 配置同步到好粮宝本地服务（jinglunGo 8765） ==================
//
// 背景: 过磅设备配置原只存浏览器 localStorage, 部分用户浏览器清缓存/策略设置导致
// 重启电脑后配置丢失（监控IP密码、地磅格式全没）, 只能重新调试。
//
// 方案: 好粮宝本地服务在线时, 以服务端 settings.json 的 weighConfig 段为持久真身:
//   - 本地每次 save* 双写 → 记时间戳, 防抖推送到服务端（/api/weigh-config, 逐 key 时间戳新者胜）;
//   - 启动/切厂时全量对账: 远端新 → 采纳回 localStorage 并派发 'weigh-config:synced' 事件
//     （GlobalScaleBar/WeighDeviceConfig 监听后重新水合）; 本地新/远端缺 → 上传;
//   - 服务不在线（纯浏览器用户/未装好粮宝）: 完全退回原 localStorage 行为, 零回归。
//
// 维度: [2026-09-26] 设备配置机器级共用 —— 全部 key 统一走服务端 '_global' 维度
// （不再按工厂分维度; scopedKey 已退化为恒等返回基础 key）。
// 服务端 settings.json 里老版本留下的各工厂段成为死数据, 无代码再读, 不做清理。

const SYNC_META_KEY = 'drying_weigh_config_meta_v1' // {baseKey: 最后写入时间戳}
const SYNCED_BASE_KEYS = [
  BIND_KEY, CAMERA_CONFIG_KEY, SCALE_PORT_POOL_KEY, SCALE_CONNECT_MODE_KEY,
  SCALE_AUTO_FOLLOW_DISABLED_KEY, SCALE_AUTO_CONNECTED_ONCE_KEY, TODAY_SUMMARY_VISIBLE_KEY,
  CAMERA_MONITOR_ENABLED_KEY, NFC_RECORD_FLOW_KEY, PRINTER_CONFIG_KEY, HARDWARE_DISABLED_KEY
]
const WEIGH_CONFIG_SYNCED_EVENT = 'weigh-config:synced'

// 同步状态（配置页显示用）
const _syncState = {
  online: false,      // 好粮宝外设服务是否在线
  lastSyncAt: 0,      // 最后一次成功同步时间戳
  inited: false
}
let _syncTimer = null      // 周期/重试定时器
let _pushDebounce = null   // markDirty 防抖
let _syncing = false       // syncNow 进行中标记（防重入）

function _loadSyncMeta() {
  try {
    const raw = localStorage.getItem(SYNC_META_KEY)
    if (raw) return JSON.parse(raw) || {}
  } catch (e) { /* ignore */ }
  return {}
}
function _saveSyncMeta(meta) {
  try { localStorage.setItem(SYNC_META_KEY, JSON.stringify(meta)) } catch (e) { /* ignore */ }
}

// [2026-09-26] 机器级化后所有 key 都用基础 key 本身（isGlobal 参数保留只为兼容
// 既有调用点签名, 不再有维度含义）
function _syncStorageKey(baseKey) {
  return baseKey
}
function _readLocalRaw(baseKey) {
  try {
    return readScoped(baseKey)
  } catch (e) { return null }
}

/**
 * 本地配置变化标记（save* 内部调用）: 记时间戳 + 防抖推送。
 * @param {string} baseKey 配置基础 key（不带工厂后缀）
 * @param {boolean} [isGlobal] 本机全局 key（走 _global 维度）
 */
function _markWeighConfigDirty(baseKey) {
  const sk = _syncStorageKey(baseKey)
  const meta = _loadSyncMeta()
  if (!meta[sk] || meta[sk] < Date.now()) meta[sk] = Date.now() // 同 ms 多次写保序
  _saveSyncMeta(meta)
  if (!_syncState.inited) return
  if (_pushDebounce) clearTimeout(_pushDebounce)
  _pushDebounce = setTimeout(() => {
    _pushDebounce = null
    syncWeighConfigNow() // 防抖 1s 后推（服务在线才会上传, 离线只留时间戳待下次同步）
  }, 1000)
}

/** 同步状态（WeighDeviceConfig 页头指示用） */
export function getWeighConfigSyncStatus() {
  return { online: _syncState.online, lastSyncAt: _syncState.lastSyncAt }
}

/**
 * 初始化同步循环（App.vue created 调用一次）。
 * 在线时每 5 分钟全量对账一次（多浏览器/换浏览器场景收敛）; 离线时 60s 重探。
 */
export function initWeighConfigSync() {
  if (_syncState.inited) return
  _syncState.inited = true
  const loop = async () => {
    const ok = await syncWeighConfigNow()
    _syncTimer = setTimeout(loop, ok ? 5 * 60 * 1000 : 60 * 1000)
  }
  // 延迟 1.5s 启动: 等登录态/工厂 ID 就绪, 不抢首屏
  _syncTimer = setTimeout(loop, 1500)
}

/**
 * 立即执行一次同步（切厂/App 启动/markDirty 防抖触发）。失败静默（离线常态）。
 * @returns {Promise<boolean>} 是否成功（服务在线且对账完成）
 */
export async function syncWeighConfigNow() {
  if (_syncing) return _syncState.online
  _syncing = true
  try {
    try {
      await ScaleClient.health()
    } catch (e) {
      _syncState.online = false
      return false // 服务未启动（纯浏览器用户）: 保持现状, 等下次重探
    }
    _syncState.online = true

    // [2026-09-26] 机器级共用: 只同步 '_global' 一个维度（不再按工厂分维度）。
    // 任何账号登录, 设备配置都是同一份。
    const dims = [
      { factory: '_global', global: true }
    ]
    const changedKeys = []

    for (const dim of dims) {
      const keys = SYNCED_BASE_KEYS
      let remoteEntries = {}
      try {
        const res = await ScaleClient.getWeighConfig(dim.factory)
        remoteEntries = (res && res.entries) || {}
      } catch (e) { continue } // 单维度失败不影响另一维度

      const meta = _loadSyncMeta()
      const pushEntries = {}
      let metaDirty = false

      const adoptRemote = (baseKey, entry) => {
        if (!entry || entry.v == null) return
        localStorage.setItem(_syncStorageKey(baseKey), entry.v)
        meta[_syncStorageKey(baseKey)] = entry.t || 0
        metaDirty = true
        changedKeys.push(baseKey)
      }

      for (const baseKey of keys) {
        const sk = _syncStorageKey(baseKey)
        const localRaw = _readLocalRaw(baseKey)
        const localT = Number(meta[sk]) || 0
        const remote = remoteEntries[baseKey]
        const remoteT = remote ? (Number(remote.t) || 0) : 0
        if (remote && remoteT > localT) {
          // 远端新（另一浏览器写过/本机浏览器缓存被清）→ 采纳
          adoptRemote(baseKey, remote)
        } else if (localRaw != null && localT >= remoteT) {
          // 本地新或远端缺 → 上传; 老数据无时间戳（localT=0）首推时领取当前时间
          const t = localT > 0 ? localT : Date.now()
          meta[sk] = t
          metaDirty = true
          pushEntries[baseKey] = { v: localRaw, t }
        }
      }

      if (Object.keys(pushEntries).length) {
        try {
          const merged = await ScaleClient.saveWeighConfig(dim.factory, pushEntries)
          // 服务端并发合并后可能有比本地更新的（另一浏览器刚写）→ 再采纳一轮
          const mergedEntries = (merged && merged.entries) || {}
          for (const baseKey of keys) {
            const sk = _syncStorageKey(baseKey)
            const entry = mergedEntries[baseKey]
            const localT = Number(meta[sk]) || 0
            if (entry && (Number(entry.t) || 0) > localT && !pushEntries[baseKey]) {
              adoptRemote(baseKey, entry)
            }
          }
        } catch (e) { /* 推送失败: 时间戳已记, 下次同步重推 */ }
      }
      if (metaDirty) _saveSyncMeta(meta)
    }

    _syncState.lastSyncAt = Date.now()
    if (changedKeys.length) {
      // 通知监听方（GlobalScaleBar 重新水合连接配置 / WeighDeviceConfig 刷新表单）
      try {
        window.dispatchEvent(new CustomEvent(WEIGH_CONFIG_SYNCED_EVENT, { detail: { changedKeys } }))
      } catch (e) { /* ignore */ }
    }
    return true
  } finally {
    _syncing = false
  }
}

export { WEIGH_CONFIG_SYNCED_EVENT }

// 供绕过本模块直接写 localStorage 的组件（如 HikvisionPreviewGrid 的摄像头配置）补挂同步标记
export { _markWeighConfigDirty as markWeighConfigDirty }
